// MIDAS V3 — Thread date-bucketing util (Phase 1)
//
// Pure function ported from v2's MidasHistoryPageV2.bucketOf (lines 94-110).
// Same 5-bucket scheme so v3 and v2 group threads identically.
//
// v3 lives in its own folder so v2 is never edited. If the v2 logic ever
// diverges, the right move is to extract v2's `bucketOf` into a shared util
// and import both — but for Phase 1 we don't touch v2 at all.
//
// Spec: MIDAS_V3_PHASE1_SCAFFOLD_SPEC.md §6 (rail uses Today/Yesterday/
// This week buckets but the underlying helper supports the full 5-bucket
// scheme so Phase 5 / history page can reuse it.)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Pure TS.

export type V3DateBucket =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "earlier";

/**
 * Same bucket boundaries as v2's `bucketOf`:
 *   today      — d >= startOfToday
 *   yesterday  — d >= startOfYesterday && d < startOfToday
 *   thisWeek   — d >= startOfWeek      && d < startOfYesterday
 *                (startOfWeek = startOfToday − 7 days)
 *   lastWeek   — d >= startOfLastWeek  && d < startOfWeek
 *                (startOfLastWeek = startOfWeek − 7 days)
 *   earlier    — d < startOfLastWeek (open-ended tail)
 */
export const bucketOfV3 = (iso: string): V3DateBucket => {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

  if (d.getTime() >= startOfToday.getTime()) return "today";
  if (d.getTime() >= startOfYesterday.getTime()) return "yesterday";
  if (d.getTime() >= startOfWeek.getTime()) return "thisWeek";
  if (d.getTime() >= startOfLastWeek.getTime()) return "lastWeek";
  return "earlier";
};

/** Bucket label fallback (English). Caller is expected to pass a translated
 *  label via i18n; this is only used as the i18next defaultValue. */
export const v3BucketFallback = (b: V3DateBucket): string => {
  switch (b) {
    case "today":
      return "Today";
    case "yesterday":
      return "Yesterday";
    case "thisWeek":
      return "This week";
    case "lastWeek":
      return "Last week";
    case "earlier":
      return "Earlier";
  }
};
